import { Application } from 'pixi.js';
import { PlayerSprite } from './playerSprite';
import { buildPlayerLayers } from './buildPlayerLayers';
import type { PlayerLook } from './buildPlayerLayers';
import { DEFAULT_BODY, type BodyFrames, type EquipFrames } from './playerArt';
import type { SpriteAtlasStore } from './spriteAtlasStore';
import { equipment as equipDefs } from '../sim/content';
import type { HueTable } from './hueTable';
import { Direction } from '../sim/direction';
import { configureUoHueBatch } from './uoHueBatch';

/**
 * Small pictures of a cosmetic or a complete outfit, for the unlock notifications
 * dropdown and favorite-outfit slots. It reuses the **exact same art path the
 * wardrobe preview does** -
 * {@link buildPlayerLayers} + {@link PlayerSprite} over the shared baked frames and
 * hue table - so the icon can never drift from what the item actually looks like
 * in-world. The item is drawn on a bare body (its own valid body, so a gender-gated
 * layer like a beard still shows), south-facing stand pose, then the rendered art is
 * cropped-and-centred onto a small fixed-size 2D canvas the notifications DOM embeds.
 *
 * It owns **one** small offscreen Pixi {@link Application} (one WebGL context, not one
 * per notification - a long AFK stretch can accumulate many unlocks), rendering each
 * requested cosmetic or assembled look once and caching the result canvas by key. It is
 * intentionally the only Pixi-touching part of the notifications feature; the feed and
 * its DOM live in the headless-testable `notifications.ts`.
 */

/** Side of the square icon canvas embedded per notification row (px). */
const ICON_SIZE = 44;
/** Never upscale the baked art past this, so a small item isn't blown up blurry. */
const MAX_SCALE = 1;
/** Padding kept inside the icon square so the art doesn't touch the edges (px). */
const ICON_PAD = 3;

export class CosmeticIconRenderer {
  private readonly app = new Application();
  // Resolves once `app.init` has completed; every render awaits it so the first call
  // races the async init cleanly and later calls are effectively synchronous.
  private ready: Promise<void> | undefined;
  // Rendered icons cached by `item hue` key - an unlock (or a re-render on re-open)
  // reuses the canvas rather than re-running the renderer.
  private readonly cache = new Map<string, HTMLCanvasElement | null>();
  private readonly outfitCache = new Map<string, HTMLCanvasElement | null>();

  constructor(
    private readonly bodyFrames: BodyFrames,
    private readonly equipFrames: EquipFrames,
    private readonly hueTable?: HueTable,
    private readonly doc: Document = document,
    // The lazy sprite-atlas store (ADR-0247): a cosmetic's equipment atlas now streams
    // on demand, so an icon must ensure its item's atlas is loaded before drawing rather
    // than assuming the whole equipment set is resident. Optional so headless tests that
    // pre-populate `equipFrames` need not wire it.
    private readonly store?: SpriteAtlasStore,
  ) {}

  private init(): Promise<void> {
    if (!this.ready) {
      if (this.hueTable) configureUoHueBatch(this.hueTable);
      // Don't let a failed init memoize a rejected promise for the whole session -
      // clear `ready` on failure so a later render can retry rather than every icon
      // being stuck. The rejection still propagates to this call's caller.
      this.ready = this.app
        .init({
          width: ICON_SIZE,
          height: ICON_SIZE,
          backgroundAlpha: 0,
          antialias: false,
          preference: 'webgl',
        })
        .catch((err) => {
          this.ready = undefined;
          throw err;
        });
    }
    return this.ready;
  }

  /**
   * Render (or return the cached) picture of `{item, hue}` as a fixed-size 2D canvas.
   * Resolves `null` for an unknown/unbaked id, or when the assembled art has no
   * drawable pixels for this facing (the row then simply shows no picture).
   */
  async render(item: string, hue: number): Promise<HTMLCanvasElement | null> {
    const key = `${item} ${hue}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;

    // Degrade quietly like the rest of the feature: a failed init or draw yields no
    // picture rather than a rejection. A failed key isn't cached, so it can retry.
    let result: HTMLCanvasElement | null = null;
    // Pin the item's atlas for the duration of the draw (issue #625): the icon copies
    // pixels onto its own cached 2D canvas, so the pin is transient - after the draw
    // the source atlas is evictable again without affecting the icon.
    const pin = this.store?.pinLook({ equipment: [{ id: item }] });
    try {
      // Stream the item's equipment atlas in first (ADR-0247), then draw. Deduped in
      // the store, so a re-render or a concurrent wearer shares the one in-flight load;
      // without a store this resolves immediately and the pre-populated map is used.
      await this.store?.loadEquip(item);
      await this.init();
      result = this.draw(item, hue);
    } catch {
      return null;
    } finally {
      pin?.();
    }
    this.cache.set(key, result);
    return result;
  }

  /**
   * Render a complete assembled outfit as one character silhouette. `facing` selects
   * the stand pose's direction (default south, the notification-icon look); the card
   * duel renders each duelist east/west so the two face each other across the arena.
   */
  async renderOutfit(look: PlayerLook, facing: Direction = Direction.S): Promise<HTMLCanvasElement | null> {
    const key = `${facing} ${JSON.stringify(look)}`;
    const cached = this.outfitCache.get(key);
    if (cached !== undefined) return cached === null ? null : this.clone(cached);
    // Transient residency pin for the outfit draw (issue #625) - see render().
    const pin = this.store?.pinLook(look);
    try {
      await Promise.all((look.equipment ?? []).map((piece) => this.store?.loadEquip(piece.id)));
      await this.init();
      const result = this.drawLook(look, facing);
      this.outfitCache.set(key, result);
      return result === null ? null : this.clone(result);
    } catch {
      return null;
    } finally {
      pin?.();
    }
  }

  /** Build the layer stack, render one stand frame, and crop-centre it onto a canvas. */
  private draw(item: string, hue: number): HTMLCanvasElement | null {
    if (!equipDefs.has(item)) return null;
    // Draw on a body the item is valid for, so a gender-gated layer (e.g. a beard,
    // `bodies:[400]`) is never silently dropped by buildPlayerLayers' body gating.
    const body = equipDefs.get(item).bodies?.[0] ?? DEFAULT_BODY;
    return this.drawLook({ body, bodyHue: 0, equipment: [{ id: item, hue }] });
  }

  /** Build and draw a full look through the same layered player renderer as the game. */
  private drawLook(look: PlayerLook, facing: Direction = Direction.S): HTMLCanvasElement | null {
    const layers = buildPlayerLayers(
      look,
      this.bodyFrames,
      this.equipFrames,
    );
    const sprite = new PlayerSprite(layers, facing);
    this.app.stage.addChild(sprite);
    let raw: HTMLCanvasElement | null = null;
    try {
      // Extract the drawn art (cropped to its own bounds, with hue filters applied by
      // the WebGL pass), then crop-and-centre it into the fixed icon square below so
      // every row's picture is the same size regardless of where the layer sits.
      const extracted = this.app.renderer.extract.canvas(sprite) as HTMLCanvasElement;
      if (extracted.width > 0 && extracted.height > 0) {
        raw = this.center(extracted);
      }
    } finally {
      this.app.stage.removeChild(sprite);
      sprite.destroy();
    }
    return raw;
  }

  /** A canvas node cannot be mounted twice, so callers receive a fresh copy of cached art. */
  private clone(source: HTMLCanvasElement): HTMLCanvasElement {
    const out = this.doc.createElement('canvas');
    out.width = source.width;
    out.height = source.height;
    out.getContext('2d')?.drawImage(source, 0, 0);
    return out;
  }

  /** Scale-to-fit and centre a cropped art canvas onto a fixed ICON_SIZE square. */
  private center(art: HTMLCanvasElement): HTMLCanvasElement {
    const out = this.doc.createElement('canvas');
    out.width = ICON_SIZE;
    out.height = ICON_SIZE;
    const ctx = out.getContext('2d');
    if (!ctx) return out;
    ctx.imageSmoothingEnabled = false; // nearest-neighbour, like the in-world art
    const avail = ICON_SIZE - ICON_PAD * 2;
    const scale = Math.min(avail / art.width, avail / art.height, MAX_SCALE);
    const w = Math.round(art.width * scale);
    const h = Math.round(art.height * scale);
    ctx.drawImage(art, (ICON_SIZE - w) / 2, (ICON_SIZE - h) / 2, w, h);
    return out;
  }

  /** Tear down the Pixi app + its context (called if the session ends). */
  destroy(): void {
    this.cache.clear();
    this.outfitCache.clear();
    this.app.destroy(true, { children: true });
  }
}
